import { useEffect, useState } from "react";
import api from "../services/api";
import { Loader2, RefreshCw, ShieldAlert, Check, X, AlertTriangle, Wallet, CreditCard, Sparkles, Search, Plus } from "lucide-react";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

/**
 * Sprint 81 — Admin Refund Lifecycle dashboard.
 *
 * Single-screen control center for every refund row across:
 *   • cancellation_refund (auto-fired)
 *   • edit_reprice (auto-fired)
 *   • manual_admin (created here)
 *   • damage_claim / sla_breach
 *
 * Actions: retry, override-hold, approve, reject, manual create.
 */
const STATUS_TABS = [
  { key: "", label: "All" },
  { key: "initiated", label: "Initiated" },
  { key: "processing", label: "Processing" },
  { key: "failed", label: "Failed" },
  { key: "on_hold", label: "On Hold" },
  { key: "completed", label: "Completed" },
  { key: "rejected", label: "Rejected" },
];

const STATUS_BADGE = {
  initiated: "bg-sky-50 text-sky-700 border-sky-200",
  processing: "bg-indigo-50 text-indigo-700 border-indigo-200",
  failed: "bg-orange-50 text-orange-700 border-orange-200",
  on_hold: "bg-amber-50 text-amber-800 border-amber-300",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
};

const METHOD_ICON = {
  wallet: Wallet,
  razorpay_source: CreditCard,
  store_credit: Sparkles,
};

export default function AdminRefundsLifecycle() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [kindFilter, setKindFilter] = useState("");
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [decideTarget, setDecideTarget] = useState(null);

  const load = async () => {
    try {
      const [qRes, sRes] = await Promise.all([
        api.get(`/admin/refunds/queue${statusFilter ? `?status=${statusFilter}` : ""}${kindFilter ? `${statusFilter ? "&" : "?"}kind=${kindFilter}` : ""}`),
        api.get("/admin/refunds/summary"),
      ]);
      setItems(qRes.data.items || []);
      setSummary(sRes.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load refund queue");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { setLoading(true); load(); /* eslint-disable-next-line */ }, [statusFilter, kindFilter]);

  const filtered = items.filter((r) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      r.tracking_id?.toLowerCase().includes(q)
      || r.refund_id?.toLowerCase().includes(q)
      || r.user_id?.toLowerCase().includes(q)
      || r.reason?.toLowerCase().includes(q)
    );
  });

  const retry = async (refund_id) => {
    setBusy(refund_id);
    try {
      await api.post(`/admin/refunds/${refund_id}/retry`);
      toast.success("Retry triggered");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Retry failed"); }
    finally { setBusy(null); }
  };

  const overrideHold = async (refund_id) => {
    const reason = window.prompt("Override reason (min 4 chars):");
    if (!reason || reason.trim().length < 4) return;
    setBusy(refund_id);
    try {
      await api.post(`/admin/refunds/${refund_id}/override-hold`, { reason });
      toast.success("Override active");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Override failed"); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-6" data-testid="admin-refunds-lifecycle-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900">Refund Lifecycle</h1>
          <p className="text-sm text-slate-500 mt-1">Unified queue · cancellations, edit-reprice, manual refunds</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} data-testid="admin-refunds-reload">
            <RefreshCw className="h-4 w-4 mr-1.5" /> Reload
          </Button>
          <Button
            onClick={() => setCreateOpen(true)}
            className="bg-[#FF6B00] hover:bg-orange-700 text-white"
            data-testid="admin-refunds-create-btn"
          >
            <Plus className="h-4 w-4 mr-1.5" /> Manual refund
          </Button>
        </div>
      </div>

      {/* Summary tiles */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="admin-refunds-summary">
          {STATUS_TABS.filter((t) => t.key).map((t) => {
            const s = summary.by_status[t.key];
            return (
              <SummaryTile
                key={t.key}
                label={t.label}
                count={s?.count || 0}
                total={s?.total || 0}
                active={statusFilter === t.key}
                onClick={() => setStatusFilter(statusFilter === t.key ? "" : t.key)}
                testId={`tile-${t.key}`}
              />
            );
          })}
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search tracking_id, refund_id, user_id, reason…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="admin-refunds-search"
          />
        </div>
        <select
          value={kindFilter}
          onChange={(e) => setKindFilter(e.target.value)}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
          data-testid="admin-refunds-kind-filter"
        >
          <option value="">All kinds</option>
          <option value="cancellation">Cancellation</option>
          <option value="edit_reprice">Edit reprice</option>
          <option value="manual_admin">Manual</option>
          <option value="damage_claim">Damage</option>
          <option value="sla_breach">SLA breach</option>
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-[#FF6B00]" />
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Refund ID</TableHead>
                <TableHead>Tracking</TableHead>
                <TableHead>Kind</TableHead>
                <TableHead>Method</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>SLA</TableHead>
                <TableHead>Retries</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-slate-400" data-testid="admin-refunds-empty">
                    No refunds match the current filters.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((r) => {
                  const Icon = METHOD_ICON[r.refund_method] || Sparkles;
                  return (
                    <TableRow key={r.refund_id} data-testid={`refund-row-${r.refund_id}`}>
                      <TableCell className="font-mono text-[11px] max-w-[160px] truncate" title={r.refund_id}>
                        {r.refund_id}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{r.tracking_id}</TableCell>
                      <TableCell className="text-xs">{r.kind}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-xs">
                          <Icon className="h-3.5 w-3.5 text-slate-600" />
                          <span>{r.refund_method}</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-bold">₹{r.amount}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded border text-[11px] font-semibold ${STATUS_BADGE[r.refund_status] || "bg-slate-50 text-slate-500"}`}>
                          {r.refund_status}
                        </span>
                      </TableCell>
                      <TableCell className="text-[11px] text-slate-500 font-mono">
                        {r.due_at?.slice(0, 16).replace("T", " ") || "—"}
                      </TableCell>
                      <TableCell className="text-xs">
                        {r.retry_count || 0}/5
                      </TableCell>
                      <TableCell className="text-right space-x-1">
                        {(r.refund_status === "failed" || r.refund_status === "on_hold") && (
                          <Button
                            size="sm" variant="outline"
                            onClick={() => retry(r.refund_id)}
                            disabled={busy === r.refund_id}
                            data-testid={`refund-retry-${r.refund_id}`}
                          >
                            {busy === r.refund_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                          </Button>
                        )}
                        {r.refund_status === "on_hold" && (
                          <Button
                            size="sm" variant="outline"
                            onClick={() => overrideHold(r.refund_id)}
                            disabled={busy === r.refund_id}
                            title="Override hold"
                            data-testid={`refund-override-${r.refund_id}`}
                          >
                            <ShieldAlert className="h-3 w-3" />
                          </Button>
                        )}
                        {(r.refund_status === "initiated" || r.refund_status === "on_hold") && (
                          <>
                            <Button
                              size="sm" variant="outline"
                              onClick={() => setDecideTarget({ ...r, decision: "approve" })}
                              data-testid={`refund-approve-${r.refund_id}`}
                            >
                              <Check className="h-3 w-3" />
                            </Button>
                            <Button
                              size="sm" variant="outline"
                              onClick={() => setDecideTarget({ ...r, decision: "reject" })}
                              data-testid={`refund-reject-${r.refund_id}`}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </>
                        )}
                        {r.last_error && (
                          <span title={r.last_error} className="ml-1">
                            <AlertTriangle className="inline h-3 w-3 text-amber-500" />
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <ManualRefundDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={load}
      />
      <DecideDialog
        target={decideTarget}
        onClose={() => setDecideTarget(null)}
        onDone={load}
      />
    </div>
  );
}

function SummaryTile({ label, count, total, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left rounded-xl border p-4 transition ${
        active
          ? "border-[#FF6B00] bg-orange-50 shadow-sm"
          : "border-slate-200 bg-white hover:border-slate-300"
      }`}
      data-testid={testId}
    >
      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</p>
      <p className="text-2xl font-extrabold text-slate-900 mt-1">{count}</p>
      <p className="text-xs text-slate-500 mt-1">₹{total.toLocaleString()}</p>
    </button>
  );
}

function ManualRefundDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ tracking_id: "", amount: "", reason: "", force_method: "" });
  const [submitting, setSubmitting] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.tracking_id || !form.amount || !form.reason || form.reason.length < 4) {
      toast.error("All fields required (reason >= 4 chars)"); return;
    }
    setSubmitting(true);
    try {
      const payload = {
        tracking_id: form.tracking_id.trim(),
        amount: parseInt(form.amount, 10),
        reason: form.reason.trim(),
      };
      if (form.force_method) payload.force_method = form.force_method;
      await api.post("/admin/refunds/manual", payload);
      toast.success("Manual refund created");
      onOpenChange(false);
      setForm({ tracking_id: "", amount: "", reason: "", force_method: "" });
      if (onCreated) onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="manual-refund-dialog">
        <DialogHeader>
          <DialogTitle>Manual refund</DialogTitle>
          <DialogDescription>Create a one-off refund (compensation, damage, gesture).</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs font-bold uppercase text-slate-500">Tracking ID</label>
            <Input value={form.tracking_id} onChange={(e) => setForm({ ...form, tracking_id: e.target.value })} data-testid="manual-refund-tid" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase text-slate-500">Amount (₹)</label>
            <Input type="number" min="1" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="manual-refund-amount" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase text-slate-500">Reason</label>
            <Textarea
              value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
              placeholder="Damage claim / SLA breach / goodwill gesture / …"
              data-testid="manual-refund-reason"
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase text-slate-500">Force method (optional)</label>
            <select
              value={form.force_method}
              onChange={(e) => setForm({ ...form, force_method: e.target.value })}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              data-testid="manual-refund-method"
            >
              <option value="">(Auto — based on order's payment method)</option>
              <option value="wallet">Wallet credit</option>
              <option value="razorpay_source">Razorpay source refund</option>
              <option value="store_credit">Store credit</option>
            </select>
          </div>
          <Button
            type="submit" disabled={submitting}
            className="w-full bg-[#FF6B00] hover:bg-orange-700 text-white"
            data-testid="manual-refund-submit"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create refund"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DecideDialog({ target, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const open = !!target;
  useEffect(() => { if (!open) setNote(""); }, [open]);
  if (!target) return null;
  const submit = async (e) => {
    e.preventDefault();
    if (note.trim().length < 4) { toast.error("Reason >= 4 chars"); return; }
    setSubmitting(true);
    try {
      await api.post(`/admin/refunds/${target.refund_id}/decide`, {
        decision: target.decision,
        admin_note: note.trim(),
      });
      toast.success(`Refund ${target.decision}d`);
      onDone();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="refund-decide-dialog">
        <DialogHeader>
          <DialogTitle>{target.decision === "approve" ? "Approve refund" : "Reject refund"}</DialogTitle>
          <DialogDescription className="font-mono text-xs break-all">{target.refund_id}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs font-bold uppercase text-slate-500">Admin note (required, audit log)</label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="refund-decide-note" />
          </div>
          <Button
            type="submit" disabled={submitting}
            className={`w-full text-white ${target.decision === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}`}
            data-testid="refund-decide-submit"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : `Confirm ${target.decision}`}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
